/* eslint-disable complexity */
import { useContext, useState, useEffect } from 'preact/hooks';
import styleSheet from '../styles/selectorFormation.module.scss';
import classNames from 'classnames';
import { TeamSelectorContext } from '../teamSelector.component';
import FootballPitch from '../../assets/graphics/FootballPitch';
import Button from '../../components/shared-components/button/Button';
import ArrowDownIcon from '../../assets/icons/ArrowDownIcon';
import RefreshIcon from '../../assets/icons/RefreshIcon';
import EditIcon from '../../assets/icons/EditIcon';
import LoadingIcon from '../../assets/icons/LoadingIcon';
import ShareButton from './ShareButton.component';
import SignInGate from '../../components/shared-components/gate/SignInGate.component';

import { useTranslation } from 'react-i18next';

import { sendAnswer } from '../apiRequests';

const styles = styleSheet.locals || {};
import FormationSelector from './FormationSelector.component';
import PlayerSelector from './PlayerSelector.component';
import TeamGrid from './TeamGrid.component';
import { getAuthHeader } from '../../helpers/authentication';
import { setNestedScrollEnabled } from '../../helpers/reactNativeWebView';

const TEAM_SIZE = 11; // Number of players required for a complete team
const SHARE_BUTTON_DELAY = 2000; // Delay before showing share button after save

/**
 * Interactive football pitch where the fan builds and shares their team.
 *
 * @returns {Element} <SelectorPitch />
 */
function SelectorPitch() {
    const { t } = useTranslation();
    // Load saved team from localStorage on mount
    const {
        selector,
        savedFormation,
        savedPlayers,
        setSavedPlayers,
        doRefetch,
        isSignedIn,
        isGated,
        interactionEvent
    } = useContext(TeamSelectorContext);
    const teamSelectorData = selector || {};

    const [selectedFormation, setSelectedFormation] = useState(
        selector.selected_formation?.name || // First try backend selected formation
            (teamSelectorData.formations &&
            teamSelectorData.formations.length > 0
                ? teamSelectorData.formations[0]?.name // Fallback to first formation in list
                : '')
    );

    const [boxPlayers, setBoxPlayers] = useState({});

    const [teamSaving, setTeamSaving] = useState(false); // Saving in prgress
    const [teamSaved, setTeamSaved] = useState(Boolean(savedPlayers?.length));
    const [isEditing, setIsEditing] = useState(false);
    const [showShareButton, setShowShareButton] = useState(false);
    const [shareButtonDelay, setShareButtonDelay] = useState(0);

    const authHeader = getAuthHeader();

    // Calculate if team is complete (has 11 players)
    const hasCompleteTeam = Object.keys(boxPlayers).length === TEAM_SIZE;

    const isClosed = selector?.state === 'closed';

    // Handle formation selection
    useEffect(() => {
        if (savedFormation?.name) {
            setSelectedFormation(savedFormation?.name);
        }
    }, [savedFormation?.name]);

    // Handle saved players population - only on initial load or when not editing
    useEffect(() => {
        // Skip if we don't have necessary data or if we're actively editing
        if (
            !savedPlayers ||
            !selectedFormation ||
            !teamSelectorData.players ||
            isEditing
        ) {
            return;
        }

        // First enrich saved players with full player data
        const enrichedPlayers = savedPlayers.map((savedPlayer) => {
            const fullPlayerData = teamSelectorData.players.find(
                (player) => player.player_id === savedPlayer.player_id
            );
            // Combine saved player data with full player data
            return {
                ...savedPlayer,
                image: fullPlayerData?.image,
                image_asset_id: fullPlayerData?.image_asset_id,
                image_url: fullPlayerData?.image_url,
                image_filename: fullPlayerData?.image_filename,
                image_text: fullPlayerData?.image_text,
                external_image_url: fullPlayerData?.external_image_url
            };
        });

        // Sort players by grid number to ensure correct order
        const sortedPlayers = enrichedPlayers.sort(
            (a, b) => a.grid_number - b.grid_number
        );

        const newBoxPlayers = {};

        // Get formation layout
        const formationObject = teamSelectorData.formations.find(
            (formation) => formation.name === selectedFormation
        );

        if (!formationObject) {
            return;
        }

        const formation = formationObject.name.split('-').map(Number);

        // Build grid rows to match the formation structure
        const gridRows = [
            { label: 'GK', count: 1 }, // Row 0: Always 1 goalkeeper
            { label: 'DEF', count: formation[0] }, // Row 1: Defenders
            ...formation.slice(1, -1).map((count, index) => ({
                label: `MID${index + 1}`, // MID1, MID2, etc.
                count
            })),
            { label: 'FWD', count: formation[formation.length - 1] } // Last row: Forwards
        ];

        // Fill grid boxes sequentially with sorted players
        let playerIndex = 0;
        gridRows.forEach((row, rowIndex) => {
            // For each column in this row
            for (let colIndex = 0; colIndex < row.count; colIndex++) {
                // If we still have players to place
                if (playerIndex < sortedPlayers.length) {
                    newBoxPlayers[`${rowIndex}-${colIndex}`] =
                        sortedPlayers[playerIndex];
                    playerIndex++;
                }
            }
        });

        setBoxPlayers(newBoxPlayers);
    }, [
        savedPlayers,
        selectedFormation,
        teamSelectorData.formations,
        teamSelectorData.players,
        teamSaved,
        isEditing
    ]);

    useEffect(() => {
        setShowShareButton(false);
        if (teamSaved) {
            setTimeout(() => {
                setShowShareButton(true);
            }, shareButtonDelay);
        }
    }, [teamSaved, shareButtonDelay]);

    // Save team to localStorage
    const handleSaveTeam = () => {
        setTeamSaving(true);
        setShareButtonDelay(SHARE_BUTTON_DELAY);

        // Get formation ID from selected formation
        const formationObj = teamSelectorData.formations.find(
            (formation) =>
                formation.name === selectedFormation ||
                formation.id === selectedFormation
        );

        // Convert boxPlayers to the required format with sequential grid numbers
        const players = Object.entries(boxPlayers)
            .sort(([a], [b]) => {
                // Sort by row first, then column
                const [aRow, aCol] = a.split('-').map(Number);
                const [bRow, bCol] = b.split('-').map(Number);
                return aRow === bRow ? aCol - bCol : aRow - bRow;
            })
            .map(([, player], index) => ({
                player_id: player.player_id,
                grid_number: index, // Use sequential index as grid number
                display_name: player.display_name
            }));

        // Prepare the payload
        const payload = {
            formation_id: formationObj.formation_id,
            players
        };

        const teamSelectorId = teamSelectorData?.selector_id;

        sendAnswer(teamSelectorId, payload, isGated, authHeader)
            .then(() => {
                setTeamSaving(false);
                setTeamSaved(true);
                setIsEditing(false); // Reset editing state after successful save
                setSavedPlayers(players);
                doRefetch(); // Trigger refetch in parent component
                interactionEvent?.send('TEAM_SELECTOR_SUBMITTED');
            })
            .catch((error) => {
                console.error(error.detail ?? error.message);
            });
    };

    // Overlay state for formation selector
    const [showFormationOverlay, setShowFormationOverlay] = useState(false);

    const handleFormationChange = (event) => {
        const newFormation = event.target.value;
        setSelectedFormation(newFormation);
        setIsEditing(true); // Mark that we're making changes

        // If we're not editing and have saved players, use those
        // Otherwise rebuild grid with current players
        // 1. Get all currently picked players (in order of box)
        const pickedPlayers = Object.values(boxPlayers).filter(Boolean);

        // 2. Get new grid size
        const formationObject = teamSelectorData.formations.find(
            (formation) =>
                formation.id === newFormation || formation.name === newFormation
        );
        const formationString =
            (formationObject &&
                (formationObject.value || formationObject.name)) ||
            '';
        const formationArray = formationString
            .split('-')
            .map((numberString) => parseInt(numberString, 10));

        // Build new gridRows
        const newGridRows = [
            { label: 'GK', count: 1 },
            { label: 'DEF', count: formationArray[0] || 0 },
            { label: 'MID', count: formationArray[1] || 0 },
            { label: 'MID2', count: formationArray[2] || 0 },
            { label: 'FWD', count: formationArray[3] || 0 } // eslint-disable-line no-magic-numbers
        ];
        // 3. Assign picked players to new grid boxes
        const newBoxPlayers = {};
        let playerIdx = 0;
        newGridRows.forEach((row, rowIndex) => {
            for (let colIndex = 0; colIndex < row.count; colIndex++) {
                if (playerIdx < pickedPlayers.length) {
                    newBoxPlayers[`${rowIndex}-${colIndex}`] =
                        pickedPlayers[playerIdx];
                    playerIdx++;
                }
            }
        });
        setBoxPlayers(newBoxPlayers);
    };

    // Helper to get formation array from selectedFormation string (e.g. "4-4-2")
    const MAX_DEFENDERS = 5;
    const MAX_MIDFIELDERS = 5;
    const MAX_FORWARDS = 5;
    const DEFAULT_FORMATION_DEF = 4;
    const DEFAULT_FORMATION_MID = 3;
    const DEFAULT_FORMATION_FWD = 3;
    const DEFAULT_FORMATION = [
        DEFAULT_FORMATION_DEF,
        DEFAULT_FORMATION_MID,
        DEFAULT_FORMATION_FWD
    ]; // GK + 3 outfield rows
    const GRID_ROW_COUNT = 5; // GK + 4 outfield rows
    const DEF_INDEX = 0;
    const MID_INDEX = 1;
    const FWD_INDEX = 2;

    const getFormationArray = () => {
        // Find the formation object by id or name
        const formationObject = teamSelectorData.formations.find(
            (formation) =>
                formation.id === selectedFormation ||
                formation.name === selectedFormation
        );
        if (!formationObject) {
            return DEFAULT_FORMATION;
        }
        // formationObject.value or .name should be like "4-1-2-1-2"
        const formationString =
            formationObject.value || formationObject.name || '';
        return formationString
            .split('-')
            .map((numberString) => parseInt(numberString, 10));
    };

    const formationArray = getFormationArray();

    // Build grid rows for GK + 4 outfield rows
    const gridRows = [
        { label: 'GK', count: 1, max: 1 },
        {
            label: 'DEF',
            count: formationArray[DEF_INDEX] || 0,
            max: MAX_DEFENDERS
        },
        {
            label: 'MID',
            count: formationArray[MID_INDEX] || 0,
            max: MAX_MIDFIELDERS
        },
        { label: 'MID2', count: formationArray[2] || 0, max: MAX_MIDFIELDERS },
        {
            label: 'FWD',
            count: formationArray[FWD_INDEX + 1] || 0,
            max: MAX_FORWARDS
        }
    ];

    // Overlay state for player selector
    const [selectedBox, setSelectedBox] = useState(null);

    // Notify React Native WebView when any modal/overlay opens or closes
    // This enables nested scroll on Android to allow proper scrolling within modals
    useEffect(() => {
        const isAnyOverlayOpen = showFormationOverlay || selectedBox !== null;

        setNestedScrollEnabled(isAnyOverlayOpen);
    }, [showFormationOverlay, selectedBox]);

    // Cleanup: disable nested scroll on unmount only
    useEffect(() => {
        return () => setNestedScrollEnabled(false);
    }, []);

    const pickedPlayerIds = Object.values(boxPlayers)
        .map((player) => player && player.player_id)
        .filter(Boolean);

    // Handler for player selection (from overlay)
    const handlePlayerSelect = (playerId, rowIndex, colIndex) => {
        const boxKey = `${rowIndex}-${colIndex}`;

        // Find if this player is already placed somewhere else
        const existingBoxKey = Object.entries(boxPlayers).find(
            // eslint-disable-next-line no-unused-vars
            ([key, player]) => player.player_id === playerId
        )?.[0];

        setBoxPlayers((prev) => {
            const updated = { ...prev };

            // If the new box already has a player, and the player is being moved from another box,
            // swap the players
            if (existingBoxKey && updated[boxKey]) {
                // Move the player currently in the new box to the old box
                updated[existingBoxKey] = updated[boxKey];
            } else if (existingBoxKey) {
                // If only moving, just clear the old box
                delete updated[existingBoxKey];
            }

            // Place the selected player in the new box
            updated[boxKey] = teamSelectorData.players.find(
                (player) => player.player_id === playerId
            );

            return updated;
        });

        setIsEditing(true); // Mark that we're making changes
        setSelectedBox(null);
    };

    const editHandler = () => {
        if (!teamSaved) {
            // If in edit mode (not saved), this is a reset action
            setBoxPlayers({});
            setIsEditing(false); // Reset editing state
        } else {
            // If team was saved, just return to edit mode without clearing
            setTeamSaved(false);
            setIsEditing(false); // Start fresh editing state
        }
    };

    // Message shared to the clipboard: the match, the selected players in order,
    // and the link to the widget.
    const buildShareMessage = () => {
        const playerNames = [...(savedPlayers || [])]
            .sort((a, b) => a.grid_number - b.grid_number)
            .map((player) => player.display_name)
            .filter(Boolean);

        const { origin, pathname } = globalThis.location;
        const widgetUrl =
            globalThis.playbook_rn_share ||
            `${origin}${pathname}#fek-${selector.selector_id}`;

        return [
            t('selector.shareIntro', {
                teamA: selector.home_team_name,
                teamB: selector.away_team_name
            }),
            ...playerNames,
            t('selector.shareQuestion'),
            widgetUrl
        ].join('\n\n');
    };

    return (
        <div className={styles.selectorFormationWrapper} part="ts-pitch">
            <style>{styleSheet.toString()}</style>

            {!isSignedIn && isGated && <SignInGate />}

            <div className={styles.selectorFormation}>
                <div className={styles.selectorFormationHeader}>
                    {showFormationOverlay && (
                        <FormationSelector
                            formations={teamSelectorData.formations}
                            selectedFormation={selectedFormation}
                            onSelect={(formationIdOrName) => {
                                handleFormationChange({
                                    target: { value: formationIdOrName }
                                });
                                setShowFormationOverlay(false);
                            }}
                            onClose={() => setShowFormationOverlay(false)}
                        />
                    )}
                    {selectedBox && (
                        <PlayerSelector
                            players={teamSelectorData.players}
                            selectedPlayers={pickedPlayerIds}
                            selectedPosition={
                                gridRows[selectedBox.rowIndex].label
                            }
                            onSelect={(playerId) =>
                                handlePlayerSelect(
                                    playerId,
                                    selectedBox.rowIndex,
                                    selectedBox.colIndex
                                )
                            }
                            onClose={() => setSelectedBox(null)}
                        />
                    )}

                    <div className={styles.selectorFormationHeader}>
                        {!isClosed &&
                            teamSelectorData.formations &&
                            teamSelectorData.formations.length > 0 && (
                                <Button
                                    onClick={() =>
                                        !teamSaved &&
                                        setShowFormationOverlay(true)
                                    }
                                    theme="tertiary"
                                    disabled={teamSaved}
                                >
                                    {t('selector.formation')}:{' '}
                                    {selectedFormation}{' '}
                                    <ArrowDownIcon
                                        className={
                                            styles.selectorFormationButtonIcon
                                        }
                                    />
                                </Button>
                            )}

                        {!isClosed && (
                            <Button
                                onClick={editHandler}
                                theme="tertiary"
                                className={styles.selectorFormationActionButton}
                            >
                                {teamSaved
                                    ? t('selector.editTeam')
                                    : t('selector.reset')}
                                {teamSaved ? (
                                    <EditIcon
                                        className={
                                            styles.selectorFormationButtonIcon
                                        }
                                    />
                                ) : (
                                    <RefreshIcon
                                        className={
                                            styles.selectorFormationButtonIcon
                                        }
                                    />
                                )}
                            </Button>
                        )}
                    </div>
                </div>
                <div className={styles.selectorFormationPitch}>
                    <div
                        className={classNames(
                            styles.selectorFormationPitchOverlay,
                            {
                                [styles.selectorFormationPitchOverlayVisible]:
                                    teamSaving
                            }
                        )}
                    >
                        {t('selector.saving')}...
                    </div>
                    <div className={styles.selectorFormationPitchLarge}>
                        <FootballPitch />
                    </div>
                    <div className={styles.selectorFormationPitchSmall}>
                        <FootballPitch size="small" />
                    </div>
                    <div className={styles.selectorFormationFormation}>
                        <TeamGrid
                            gridRows={gridRows.slice(0, GRID_ROW_COUNT)}
                            boxPlayers={boxPlayers}
                            selectedBox={selectedBox}
                            onBoxClick={(rowIndex, colIndex) =>
                                !teamSaved &&
                                setSelectedBox({ rowIndex, colIndex })
                            }
                            editMode={!teamSaved}
                        />
                    </div>
                </div>

                <div className={styles.selectorFormationFooter}>
                    {teamSaved ? (
                        <div className={styles.selectorFormationSaveText}>
                            {teamSaved &&
                                !showShareButton &&
                                t('selector.teamSaved')}
                            {showShareButton && teamSaved && !isClosed && (
                                <ShareButton
                                    message={buildShareMessage()}
                                    onShare={() =>
                                        interactionEvent?.send(
                                            'TEAM_SELECTOR_SHARED'
                                        )
                                    }
                                />
                            )}
                        </div>
                    ) : (
                        <Button
                            theme="primary"
                            className={styles.selectorFormationSaveButton}
                            onClick={handleSaveTeam}
                            disabled={!hasCompleteTeam}
                        >
                            {teamSaving ? (
                                <LoadingIcon />
                            ) : (
                                t('selector.saveTeam')
                            )}
                        </Button>
                    )}

                    {isClosed && (
                        <div className={styles.selectorFormationClosedText}>
                            {t('selector.closedText')}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SelectorPitch;
